'use client'

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
// This rule is disabled because the dialog is not interactive,
// It's just a container and needs a control over children clicks events.
import {
	type Dispatch,
	type SetStateAction,
	forwardRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
} from 'react'
import { Keyboard } from 'types/keyboard'
import { cx } from 'utils/cx'

import { OverDialogLayerDialog } from './OverDialogLayer/OverDialogLayer'
import { lockBodyScroll, unlockBodyScroll } from './utils/lockBodyScroll'

import styles from './Dialog.module.scss'

/* eslint react-hooks/exhaustive-deps: warn */

const DIALOG = 'dialog'
const ALERT = 'alert'

interface DialogPros {
	readonly title: string
	readonly containerClassName?: string
	readonly children: React.ReactNode
	readonly preventClose?: boolean
	readonly className?: string
	readonly opened: boolean
	readonly 'data-testid'?: string
	readonly setOpened: Dispatch<SetStateAction<boolean>>
	readonly onClose?: () => void
	readonly onOpen?: () => void
	readonly location?: {
		top?: string
		left?: string
		right?: string
		bottom?: string
	}
}

export interface DialogAPI {
	show: () => void
	close: () => void
	open: boolean
	element: HTMLDialogElement | null
}

export function apiFactory(
	setOpened: Dispatch<SetStateAction<boolean>>,
	isOpen: boolean,
	element: HTMLDialogElement | null
): DialogAPI {
	return {
		show: () => {
			setOpened(true)
		},
		close: () => {
			setOpened(false)
		},
		open: isOpen,
		element,
	}
}

function DialogComponent(
	{
		title,
		containerClassName = '',
		children,
		onClose,
		onOpen,
		opened,
		preventClose = false,
		className = '',
		setOpened,
		location,
		'data-testid': dataTestId,
	}: DialogPros,
	ref: React.Ref<HTMLDialogElement | null>
) {
	const dialogRef = useRef<HTMLDialogElement>(null)
	const animationRef = useRef<HTMLDivElement>(null)

	const close = useCallback(() => {
		setOpened(false)
	}, [setOpened])

	const openDialog = useCallback(() => {
		lockBodyScroll()
		dialogRef.current?.showModal()
		onOpen?.()
	}, [onOpen])

	const closeDialog = useCallback(() => {
		onClose?.()
		dialogRef.current?.close()
		unlockBodyScroll()
	}, [onClose])

	// Exposing Dialog API to parent.
	useImperativeHandle(ref, () => dialogRef.current, [])

	const cancelHandle = useCallback(
		(e: Event) => {
			e.preventDefault()
			e.stopPropagation()
			if (preventClose) {
				return
			}
			close()
		},
		[close, preventClose]
	)

	const clickHandle = useCallback(
		(e: MouseEvent) => {
			if (e.target !== dialogRef.current || preventClose) {
				return
			}
			e.preventDefault()
			close()
		},
		[close, preventClose]
	)

	useEffect(() => {
		const isElementOpened = Boolean(dialogRef.current?.open)
		if (opened && !isElementOpened) {
			openDialog()
		}
		if (!opened && isElementOpened) {
			closeDialog()
		}
	}, [closeDialog, openDialog, opened])

	useEffect(() => {
		if (opened) {
			dialogRef.current?.focus()
		}
	}, [opened])

	useEffect(() => {
		const reference = dialogRef.current
		const keyDownHandle = (e: KeyboardEvent) => {
			if (e.key === Keyboard.Escape && preventClose) {
				e.preventDefault()
			}
		}
		reference?.addEventListener('keydown', keyDownHandle)
		reference?.addEventListener('cancel', cancelHandle)
		reference?.addEventListener('click', clickHandle)
		return () => {
			reference?.removeEventListener('cancel', cancelHandle)
			reference?.removeEventListener('click', clickHandle)
			reference?.removeEventListener('keydown', keyDownHandle)
		}
	}, [cancelHandle, clickHandle, preventClose])

	const dialogRole = !preventClose ? DIALOG : ALERT

	return (
		<dialog
			data-testid={dataTestId}
			className={cx(styles.dialog, className)}
			style={location}
			aria-label={title}
			ref={dialogRef}
			role={dialogRole}
		>
			<div
				data-dialog-container
				ref={animationRef}
				className={containerClassName}
			>
				{opened && children}
			</div>
			{opened && <OverDialogLayerDialog />}
		</dialog>
	)
}
export const Dialog = forwardRef(DialogComponent)
